(() => {
  const roots = document.querySelectorAll('[data-landos-root]');
  if (!roots.length) return;

  const weekdayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

  roots.forEach((root) => {
    setupMenu(root);
    setupHours(root);
  });

  function setupMenu(root) {
    const button = root.querySelector('[data-menu-toggle]');
    const menu = root.querySelector('[data-menu]');
    if (!button || !menu) return;

    button.addEventListener('click', () => {
      const isOpen = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!isOpen));
      menu.classList.toggle('is-open', !isOpen);
    });

    menu.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        button.setAttribute('aria-expanded', 'false');
        menu.classList.remove('is-open');
      });
    });
  }

  function setupHours(root) {
    const status = root.querySelector('[data-open-status]');
    const statusLabel = root.querySelector('[data-open-label]');
    const next = root.querySelector('[data-open-next]');
    const list = root.querySelector('[data-hours-list]');
    const footnote = root.querySelector('[data-hours-footnote]');
    const attribution = root.querySelector('[data-google-attribution]');
    if (!status || !statusLabel || !next || !list || !footnote) return;

    const apiKey = root.dataset.apiKey && root.dataset.apiKey.trim();
    const placeId = root.dataset.placeId && root.dataset.placeId.trim();
    if (!apiKey || !placeId) {
      renderFallbackStatus(root, status, statusLabel, next);
      window.setInterval(() => renderFallbackStatus(root, status, statusLabel, next), 60_000);
      footnote.textContent = 'Google ist noch nicht verbunden; bis dahin gelten die festen Ersatzzeiten.';
      return;
    }

    renderFallbackStatus(root, status, statusLabel, next);
    const fallbackTimer = window.setInterval(() => renderFallbackStatus(root, status, statusLabel, next), 60_000);

    const endpoint = `https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}?key=${encodeURIComponent(apiKey)}&languageCode=de`;
    fetch(endpoint, {
      method: 'GET',
      headers: {
        'X-Goog-FieldMask': 'currentOpeningHours',
        'Accept-Language': 'de'
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Places API returned ${response.status}`);
        return response.json();
      })
      .then((place) => {
        const openingHours = place && place.currentOpeningHours;
        if (!openingHours) throw new Error('No current opening hours in response');
        if (typeof openingHours.openNow !== 'boolean') throw new Error('No open status in response');

        window.clearInterval(fallbackTimer);

        if (Array.isArray(openingHours.weekdayDescriptions) && openingHours.weekdayDescriptions.length) {
          renderGoogleHours(list, openingHours.weekdayDescriptions);
        }

        status.classList.toggle('is-open', openingHours.openNow);
        status.classList.toggle('is-closed', !openingHours.openNow);
        statusLabel.textContent = openingHours.openNow ? 'Jetzt geöffnet' : 'Jetzt geschlossen';
        next.textContent = openingHours.openNow
          ? timeHint('Schließt', openingHours.nextCloseTime)
          : timeHint('Öffnet', openingHours.nextOpenTime);

        if (attribution) attribution.hidden = false;
        footnote.textContent = 'Google zeigt die kommenden sieben Tage einschließlich besonderer Öffnungszeiten und Feiertage.';
      })
      .catch(() => {
        footnote.textContent = 'Google konnte gerade nicht geladen werden; die festen Ersatzzeiten werden angezeigt.';
        renderFallbackStatus(root, status, statusLabel, next);
      });
  }

  function renderGoogleHours(list, descriptions) {
    const currentDay = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', weekday: 'long' })
      .format(new Date())
      .toLocaleLowerCase('de-DE');
    const rows = descriptions.map((description) => {
      const li = document.createElement('li');
      li.textContent = description;
      const normalized = description.trim().toLocaleLowerCase('de-DE');
      if (normalized.startsWith(currentDay)) li.classList.add('is-today');
      return li;
    });
    list.replaceChildren(...rows);
  }

  function renderFallbackStatus(root, status, label, next) {
    const nowParts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Berlin',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date());
    const values = Object.fromEntries(nowParts.map((part) => [part.type, part.value]));
    const dayIndexes = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const todayIndex = dayIndexes[values.weekday];
    const currentMinutes = Number(values.hour) * 60 + Number(values.minute);
    const todayRow = root.querySelector(`[data-fallback-day="${todayIndex}"]`);
    const todayText = todayRow && todayRow.querySelector('[data-fallback-hours]');
    const ranges = parseRanges(todayText ? todayText.textContent : '');
    const activeRange = ranges.find((range) => currentMinutes >= range.open && currentMinutes < range.close);

    root.querySelectorAll('[data-fallback-day]').forEach((row) => {
      row.classList.toggle('is-today', Number(row.dataset.fallbackDay) === todayIndex);
    });

    if (activeRange) {
      status.classList.add('is-open');
      status.classList.remove('is-closed');
      label.textContent = 'Jetzt geöffnet';
      next.textContent = `Schließt heute um ${formatMinutes(activeRange.close)} Uhr.`;
      return;
    }

    status.classList.add('is-closed');
    status.classList.remove('is-open');
    label.textContent = 'Jetzt geschlossen';
    const upcoming = findNextOpening(root, todayIndex, currentMinutes);
    next.textContent = upcoming ? `Öffnet ${upcoming.day} um ${formatMinutes(upcoming.time)} Uhr.` : '';
  }

  function parseRanges(value) {
    const ranges = [];
    const pattern = /(\d{1,2}):(\d{2})\s*[–—-]\s*(\d{1,2}):(\d{2})/g;
    let match;
    while ((match = pattern.exec(value)) !== null) {
      ranges.push({
        open: Number(match[1]) * 60 + Number(match[2]),
        close: Number(match[3]) * 60 + Number(match[4])
      });
    }
    return ranges;
  }

  function findNextOpening(root, todayIndex, currentMinutes) {
    for (let offset = 0; offset < 7; offset += 1) {
      const dayIndex = (todayIndex + offset) % 7;
      const row = root.querySelector(`[data-fallback-day="${dayIndex}"]`);
      const hours = row && row.querySelector('[data-fallback-hours]');
      const ranges = parseRanges(hours ? hours.textContent : '');
      const nextRange = ranges.find((range) => offset > 0 || range.open > currentMinutes);
      if (nextRange) {
        const day = offset === 0 ? 'heute' : offset === 1 ? 'morgen' : weekdayNames[dayIndex];
        return { day, time: nextRange.open };
      }
    }
    return null;
  }

  function formatMinutes(totalMinutes) {
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
    const minutes = String(totalMinutes % 60).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  function timeHint(label, timestamp) {
    if (!timestamp) return '';
    const time = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp));
    return `${label} um ${time} Uhr.`;
  }
})();
